import { get, set } from "idb-keyval";
import { useEffect, useState } from "react";
import type { File } from "react-pdf/dist/esm/shared/types.js";

const PDF_CACHE_PREFIX = "pdf-cache";

type CacheEntry = {
	data: Blob;
	uploadedAt: string;
};

// Test if IndexedDB is fully supported and available
const testIndexedDBSupport = async (): Promise<boolean> => {
	try {
		// Try to use IndexedDB with a simple test
		const testKey = `${PDF_CACHE_PREFIX}-test`;
		const testData = new Blob(["test"], { type: "text/plain" });
		await set(testKey, { data: testData, uploadedAt: "test" });
		await get(testKey);
		return true;
	} catch (error) {
		console.warn("IndexedDB not fully supported or available:", error);
		return false;
	}
};

export function usePdfFileCache({
	downloadUrl,
	uploadedAt,
	idPrefix,
}: {
	downloadUrl?: string;
	uploadedAt?: Date | string | null;
	idPrefix: string;
}) {
	const [state, setState] = useState<{
		file: File | null;
		isLoading: boolean;
		error?: Error;
	}>({
		file: null,
		isLoading: true,
	});
	const [indexedDBSupported, setIndexedDBSupported] = useState<boolean | null>(
		null,
	);

	// Check IndexedDB support once on mount
	useEffect(() => {
		if (indexedDBSupported === null) {
			testIndexedDBSupport().then(setIndexedDBSupported);
		}
	}, [indexedDBSupported]);

	useEffect(() => {
		if (!downloadUrl) {
			setState({ file: null, isLoading: false });
			return;
		}

		const cacheKey = `${PDF_CACHE_PREFIX}-${idPrefix}`;
		const url = downloadUrl; // Store in local const to ensure TypeScript knows it's not undefined

		async function fetchDirectlyWithoutCache() {
			try {
				const response = await fetch(url);
				if (!response.ok) {
					throw new Error(
						`Failed to fetch PDF: ${response.status} ${response.statusText}`,
					);
				}
				const pdfBlob = await response.blob();
				setState({ file: pdfBlob, isLoading: false });
			} catch (error) {
				console.error("PDF direct fetch error:", error);
				// Last resort fallback to direct URL string
				setState({
					file: url as unknown as File,
					isLoading: false,
					error:
						error instanceof Error
							? error
							: new Error("Unknown PDF fetch error"),
				});
			}
		}

		async function checkAndLoadCache() {
			// Skip cache logic if IndexedDB is not supported
			if (indexedDBSupported === false) {
				fetchDirectlyWithoutCache();
				return;
			}

			try {
				setState((prev) => ({ ...prev, isLoading: true }));
				// Try to get cached data
				const cache = await get<CacheEntry>(cacheKey);

				// Check if cache exists and is valid
				if (cache && cache.uploadedAt === uploadedAt?.toString()) {
					// Create blob URL from cached data
					setState({ file: cache.data, isLoading: false });
				} else {
					// If no cache or expired, fetch and cache the PDF
					const response = await fetch(url);
					if (!response.ok) {
						throw new Error(
							`Failed to fetch PDF: ${response.status} ${response.statusText}`,
						);
					}
					const pdfBlob = await response.blob();

					try {
						// Try to store in cache, but don't fail if this errors
						await set(cacheKey, {
							data: pdfBlob,
							uploadedAt: uploadedAt?.toString() ?? "",
						});
					} catch (cacheError) {
						console.warn("Failed to cache PDF:", cacheError);
						// Cache error doesn't stop us from returning the blob
					}

					// Set the blob directly
					setState({ file: pdfBlob, isLoading: false });
				}
			} catch (error) {
				console.error("PDF cache error:", error);
				// Fall back to direct fetch without caching
				fetchDirectlyWithoutCache();
			}
		}

		// Wait until we know if IndexedDB is supported before proceeding
		if (indexedDBSupported !== null) {
			checkAndLoadCache();
		}
	}, [downloadUrl, uploadedAt, idPrefix, indexedDBSupported]);

	return state;
}
